import React, { useState, useEffect } from 'react'
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material'
import { useTranslation } from 'react-i18next'
import hoursData from '../../../../src/data/hours.json'
import MyButton from '../../../components/buttons'
import { MdAddCircle, MdDelete } from 'react-icons/md'
import { Announce } from '../../../interfaces/announce'
import TimeSelector from '../../../components/TimeSelector'

const daysOfWeek = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

const sortDays = (days: string[]) => {
  return days.sort((a, b) => daysOfWeek.indexOf(a) - daysOfWeek.indexOf(b))
}

interface UserSchedulesFormProps {
  formData: any
  handleFieldChange: (field: string, value: any) => void
}

interface Availability {
  isAvailable: boolean
  timings: { start: string; end: string }[]
}

const UserSchedulesForm: React.FC<UserSchedulesFormProps> = () => {
  const { t } = useTranslation()
  const hours: { [key: string]: string } = hoursData.hours
  const [isAvailable, setIsAvailable247] = useState(false)
  const [isCustomHours, setIsCustomHours] = useState(true)
  const [, setIsStepValid] = useState(false)
  const [availability, setAvailability] = useState<
    Record<string, Availability>
  >(
    daysOfWeek.reduce((acc, day) => {
      acc[day] = { isAvailable: false, timings: [] }
      return acc
    }, {} as Record<string, Availability>),
  )
  const [currentTiming, setCurrentTiming] = useState({ start: '', end: '' })
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  const getStorageKey = () => {
    const currentPath = window.location.pathname
    if (currentPath === '/dashboard/announce-work/') {
      return 'myJobOffer'
    } else if (currentPath === '/dashboard/announce-event/') {
      return 'myEvent'
    } else if (currentPath === '/dashboard/announce-establishment/') {
      return 'myEstablishment'
    }
    return 'myAnnounce'
  }

  useEffect(() => {
    const storageKey = getStorageKey()
    const myAnnounce: Announce = JSON.parse(
      localStorage.getItem(storageKey) || '{}',
    )

    if (myAnnounce.hasOwnProperty('isAvailable')) {
      const is247 = myAnnounce.isAvailable
      setIsAvailable247(is247)
      setIsCustomHours(!is247)
    } else {
      setIsAvailable247(false)
      setIsCustomHours(true)
    }

    const updatedAvailability: Record<string, Availability> = {
      ...availability,
    }

    const sortedDisponibility = sortDays(myAnnounce.disponibility || [])

    daysOfWeek.forEach(day => {
      const dayIndexInDisponibility = sortedDisponibility.indexOf(day)
      const isAvailable = sortedDisponibility.includes(day)

      if (isAvailable) {
        const start =
          myAnnounce.availabilityStart?.[dayIndexInDisponibility] || '00:00'
        const end =
          myAnnounce.availabilityEnd?.[dayIndexInDisponibility] || '00:00'

        updatedAvailability[day] = {
          isAvailable: start === '00:00' && end === '00:00',
          timings: [{ start, end }],
        }
      } else {
        updatedAvailability[day] = { isAvailable: false, timings: [] }
      }
    })

    setAvailability(updatedAvailability)

    const firstNon247Day = daysOfWeek.find(
      day => !updatedAvailability[day].isAvailable,
    )
    setSelectedDay(firstNon247Day || daysOfWeek[0])
  }, [])

  useEffect(() => {
    const validateStep = () => {
      setIsStepValid(isAvailable || isCustomHours)
    }
    validateStep()
  }, [isAvailable, isCustomHours])

  useEffect(() => {
    const storageKey = getStorageKey()
    const myAnnounce = JSON.parse(localStorage.getItem(storageKey) || '{}')
    myAnnounce.isAvailable = isAvailable
    localStorage.setItem(storageKey, JSON.stringify(myAnnounce))
  }, [isAvailable])

  const handleAvailable247Change = () => {
    const newIsAvailable = !isAvailable
    setIsAvailable247(newIsAvailable)
    setIsCustomHours(!newIsAvailable)

    const storageKey = getStorageKey()
    const myAnnounce = JSON.parse(localStorage.getItem(storageKey) || '{}')
    myAnnounce.isAvailable = newIsAvailable
    localStorage.setItem(storageKey, JSON.stringify(myAnnounce))
  }

  const handleCustomHoursChange = () => {
    const newIsCustomHours = !isCustomHours
    setIsCustomHours(newIsCustomHours)
    setIsAvailable247(!newIsCustomHours)

    const storageKey = getStorageKey()
    const myAnnounce = JSON.parse(localStorage.getItem(storageKey) || '{}')
    myAnnounce.isAvailable = !newIsCustomHours
    localStorage.setItem(storageKey, JSON.stringify(myAnnounce))
  }

  const handleDayAvailabilityChange = (day: string) => {
    setAvailability(prevAvailability => {
      const updatedAvailability = { ...prevAvailability }
      const isCurrentlyAvailable = updatedAvailability[day].isAvailable
      updatedAvailability[day].isAvailable = !isCurrentlyAvailable

      const storageKey = getStorageKey()
      const myAnnounce: Announce = JSON.parse(
        localStorage.getItem(storageKey) || '{}',
      )

      if (!isCurrentlyAvailable) {
        updatedAvailability[day].timings = [{ start: '00:00', end: '00:00' }]

        myAnnounce.disponibility = sortDays([
          ...(myAnnounce.disponibility || []),
          day,
        ])
        myAnnounce.availabilityStart = [
          ...(myAnnounce.availabilityStart || []),
          '00:00',
        ]
        myAnnounce.availabilityEnd = [
          ...(myAnnounce.availabilityEnd || []),
          '00:00',
        ]
      } else {
        updatedAvailability[day].timings = []
        const dayIndex = myAnnounce.disponibility?.indexOf(day)

        if (dayIndex !== -1) {
          myAnnounce.disponibility = myAnnounce.disponibility.filter(
            d => d !== day,
          )
          myAnnounce.availabilityStart = myAnnounce.availabilityStart.filter(
            (_, index) => index !== dayIndex,
          )
          myAnnounce.availabilityEnd = myAnnounce.availabilityEnd.filter(
            (_, index) => index !== dayIndex,
          )
        }
      }

      localStorage.setItem(storageKey, JSON.stringify(myAnnounce))
      setSelectedDay(day)

      return updatedAvailability
    })
  }

  const handleTimingChange = (type: 'start' | 'end', value: string) => {
    setCurrentTiming(prev => ({ ...prev, [type]: value }))
  }

  const handleAddTiming = () => {
    if (selectedDay && currentTiming.start && currentTiming.end) {
      const updatedAvailability = { ...availability }
      updatedAvailability[selectedDay].timings = [
        { start: currentTiming.start, end: currentTiming.end },
      ]
      setAvailability(updatedAvailability)

      const storageKey = getStorageKey()
      const myAnnounce: Announce = JSON.parse(
        localStorage.getItem(storageKey) || '{}',
      )
      if (!myAnnounce.disponibility) {
        myAnnounce.disponibility = []
      }
      if (!myAnnounce.availabilityStart) {
        myAnnounce.availabilityStart = []
      }
      if (!myAnnounce.availabilityEnd) {
        myAnnounce.availabilityEnd = []
      }

      if (!myAnnounce.disponibility.includes(selectedDay)) {
        myAnnounce.disponibility.push(selectedDay)
      }

      const dayIndex = myAnnounce.disponibility.indexOf(selectedDay)
      if (dayIndex !== -1) {
        myAnnounce.availabilityStart[dayIndex] = currentTiming.start
        myAnnounce.availabilityEnd[dayIndex] = currentTiming.end
      } else {
        myAnnounce.availabilityStart.push(currentTiming.start)
        myAnnounce.availabilityEnd.push(currentTiming.end)
      }

      myAnnounce.disponibility = sortDays(myAnnounce.disponibility)
      localStorage.setItem(storageKey, JSON.stringify(myAnnounce))
      setCurrentTiming({ start: '', end: '' })
    }
  }

  const handleRemoveTiming = (day: string) => {
    const updatedAvailability = { ...availability }
    updatedAvailability[day].timings = []
    setAvailability(updatedAvailability)

    const storageKey = getStorageKey()
    const myAnnounce: Announce = JSON.parse(
      localStorage.getItem(storageKey) || '{}',
    )
    const dayIndex = myAnnounce.disponibility.indexOf(day)
    if (dayIndex !== -1) {
      myAnnounce.disponibility.splice(dayIndex, 1)
      myAnnounce.availabilityStart.splice(dayIndex, 1)
      myAnnounce.availabilityEnd.splice(dayIndex, 1)
    }

    localStorage.setItem(storageKey, JSON.stringify(myAnnounce))
  }

  const handleReset = (event: { preventDefault: () => void }) => {
    event.preventDefault()
    setIsAvailable247(false)
    setIsCustomHours(true)
    const resetAvailability = daysOfWeek.reduce((acc, day) => {
      acc[day] = { isAvailable: false, timings: [] }
      return acc
    }, {} as Record<string, { isAvailable: boolean; timings: { start: string; end: string }[] }>)

    setAvailability(resetAvailability)
    setCurrentTiming({ start: '', end: '' })
    setSelectedDay(null)

    const storageKey = getStorageKey()
    const myAnnounce: Announce = JSON.parse(
      localStorage.getItem(storageKey) || '{}',
    )
    myAnnounce.availabilityStart = []
    myAnnounce.availabilityEnd = []
    myAnnounce.disponibility = []
    localStorage.setItem(storageKey, JSON.stringify(myAnnounce))
  }

  return (
    <div>
      <div>
        <FormGroup>
          <div className='gap-2 md:gap-5 w-full flex flex-col sm:flex-row'>
            <div className='justify-center text-anthracite bg-grisclair flex w-full rounded-lg  sm:mb-0'>
              <FormControlLabel
                onChange={handleAvailable247Change}
                control={<Checkbox checked={isAvailable} />}
                label={t('create-ad.available247')}
              />
            </div>
            <div className='justify-center text-anthracite bg-grisclair flex w-full rounded-lg'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCustomHours}
                    onChange={handleCustomHoursChange}
                  />
                }
                label={t('create-ad.custom-schedule')}
              />
            </div>
          </div>
        </FormGroup>
      </div>

      {!isAvailable && isCustomHours && (
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-2 relative'>
          {daysOfWeek.map(day => (
            <div key={day} className='rounded-lg pr-2 pt-2'>
              <div className='flex justify-between items-center w-full text-white bg-anthracite py-1 px-5 rounded-t-lg'>
                <span className='uppercase font-bold'>
                  {t(`create-ad.${day}`)}
                </span>

                <div className='flex items-center'>
                  <span className='pt-0.5 -mr-4'>24h/24</span>
                  <FormControlLabel
                    label=''
                    labelPlacement='start'
                    control={
                      <Checkbox
                        checked={availability[day]?.isAvailable || false}
                        onChange={() => handleDayAvailabilityChange(day)}
                        className='text-white'
                        sx={{
                          '& .MuiSvgIcon-root': { color: '#ff005a' },
                          '& .MuiCheckbox-root': { borderColor: 'white' },
                        }}
                      />
                    }
                    className='uppercase m-0 p-0'
                  />
                </div>
              </div>
              {availability[day]?.isAvailable && (
                <div className='p-3 text-center border border-anthracite rounded-b-lg -mt-1'>
                  <span className='text-sm text-anthracite'>
                    {t('create-ad.available-all-day')}
                  </span>
                </div>
              )}
              {!availability[day]?.isAvailable && (
                <div className='p-3 justify-center text-center pt-4 border border-anthracite rounded-b-lg -mt-1'>
                  {availability[day]?.timings.map((timing, index) => (
                    <div key={index} className='flex justify-between mb-0'>
                      <span className='w-full flex justify-between bg-grisclair rounded p-2 pl-3'>
                        <span className='p-1'>{`${timing.start} - ${timing.end}`}</span>
                        <button
                          className='h-8 bg-redpink-anthracite text-redpink rounded'
                          onClick={() => handleRemoveTiming(day)}
                        >
                          <MdDelete className='text-xl' />
                        </button>
                      </span>
                    </div>
                  ))}
                  {selectedDay === day &&
                    availability[day]?.timings.length === 0 && (
                      <TimeSelector
                        selectedDay={selectedDay}
                        day={day}
                        availability={availability}
                        currentTiming={currentTiming}
                        handleTimingChange={handleTimingChange}
                        handleAddTiming={handleAddTiming}
                        hours={hours}
                        t={t}
                      />
                    )}
                  {selectedDay !== day &&
                    availability[day]?.timings.length === 0 && (
                      <button
                        className='text-redpink'
                        onClick={() => setSelectedDay(day)}
                      >
                        <MdAddCircle className='text-4xl' />
                      </button>
                    )}
                </div>
              )}
            </div>
          ))}
          <div className='mt-2'>
            {!isAvailable && (
              <MyButton
                style={{ padding: '11px 20px', width: '98%', fontSize: '1rem' }}
                variant='redpink-anthracite'
                onClick={handleReset}
              >
                {t('create-ad.reset')}
              </MyButton>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default UserSchedulesForm
